import { useMemo } from "react";

import { useGeographies } from "~/features/clientLibrary/useGeographies";
import { extractLevelFromGeography } from "~/utils/extractLevelFromGeography";
import { mapUtils } from "@local-logic/core/utils";

import { type Location } from "~/types/sdks";
import { type LocationInfo } from "~/types/profiles";
import { type Geographies } from "@local-logic/client";

type GeoLocationInfoProps = Location & {
  externalId?: string;
  locale: "en" | "fr";
  include?: "geometry";
};

/**
 * This hook is used by Neighborhood Wrap SDKs in order to display neighborhood info specific information
 */
export function useGeoLocationInfo({
  lat,
  lng,
  geographyId,
  externalId,
  locale,
  include,
}: GeoLocationInfoProps): {
  isLoading: boolean;
  geographies?: Geographies["geographies"];
  locationInfo?: LocationInfo;
} {
  const latLng = useMemo(() => {
    if (!geographyId) {
      return { lat, lng };
    }

    return mapUtils.decodeGeogId(geographyId);
  }, [lat, lng, geographyId]);

  const { data: geographies, isFetching } = useGeographies({
    location: latLng,
    externalId,
    include,
  });

  const locationInfo = useMemo(() => {
    if (!geographies || isFetching) return undefined;

    const geogKeys = Object.keys(geographies).sort(
      (a: string, b: string) =>
        Number(a.substring(1, 3)) - Number(b.substring(1, 3))
    );
    const level = Number(
      (geographyId &&
        geogKeys
          .find((key) => key.substring(1, 3) === geographyId.substring(1, 3))
          ?.substring(1, 3)) ||
        geogKeys[0]?.substring(1, 3)
    );

    return {
      coordinates: latLng,
      geogID: geographyId || geogKeys[0],
      microLocation:
        extractLevelFromGeography(
          locale,
          level,
          geographies,
          geographyId || ""
        ) || "",
      macroLocation:
        (level < 30 && extractLevelFromGeography(locale, 30, geographies)) ||
        "",
    };
  }, [geographies, isFetching]);

  return {
    isLoading: isFetching,
    geographies,
    locationInfo,
  };
}
